import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { Lock } from 'lucide-react-native';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import { SoccerKit } from '../../ui/SoccerKit';
import { resolveLeaderboardFuturesDisplay } from '../PicksView/futuresCardDisplay.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { LEADERBOARD_CARD_COPY, type LeaderboardTeamVisual } from './LeaderboardCard.constants';
import { LeaderboardUserAvatar } from './LeaderboardUserAvatar';
import { TREND_ICONS } from './WorldCupLeaderboard.constants';
import type { getWorldCupLeaderboardStyles } from './WorldCupLeaderboard.styles';
import type { LeaderboardEntry } from '../../../src/worldcup/types/worldcup.types';
import type { WorldCupFuturesOptions } from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';

export const LeaderboardRow: React.FC<{
  entry: LeaderboardEntry;
  styles: ReturnType<typeof getWorldCupLeaderboardStyles>;
  futuresOptions: WorldCupFuturesOptions | null;
  /** Flags (national teams) or kits (clubs); defaults to flags, i.e. the World Cup (AC-3.6). */
  teamVisual?: LeaderboardTeamVisual;
}> = ({ entry, styles, futuresOptions, teamVisual = 'flag' }) => {
  const theme = useTheme();
  const resolved = useMemo(
    () =>
      resolveLeaderboardFuturesDisplay(
        entry.winnerTeamId,
        entry.topScorerPlayerId,
        futuresOptions,
      ),
    [entry.winnerTeamId, entry.topScorerPlayerId, futuresOptions],
  );
  // An empty code is what `CountryFlag`'s kit fallback already keys off, so kit mode simply
  // withholds the code rather than adding a second branch (AC-3.6).
  const winnerCode =
    teamVisual === 'kit' ? undefined : resolved.winnerCountryCode ?? undefined;
  const winnerKitFallback = useMemo(
    () => getTeamKitConfig(resolved.winnerCaption ?? ''),
    [resolved.winnerCaption],
  );

  const rankStyle =
    entry.rank === 1
      ? styles.rankTextGold
      : entry.rank === 2
        ? styles.rankTextSilver
        : entry.rank === 3
          ? styles.rankTextBronze
          : styles.rankText;

  return (
    <View style={[styles.row, entry.isCurrentUser && styles.rowMe]}>
      <Text style={[styles.rankText, rankStyle]}>{entry.rank}</Text>
      <LeaderboardUserAvatar
        entry={entry}
        variant="row"
        styles={styles}
      />
      <Text style={[styles.nameText, entry.isCurrentUser && styles.nameTextMe]}>{entry.name}</Text>
      <View style={styles.futuresWrap}>
        {entry.futuresHidden ? (
          <>
            <View
              style={styles.futureCell}
              accessibilityLabel={LEADERBOARD_CARD_COPY.LOCKED_A11Y}
            >
              <Lock size={16} color={theme.gray[500]} />
            </View>
            <View style={styles.futureCell}>
              <Lock size={16} color={theme.gray[500]} />
            </View>
          </>
        ) : (
          <>
            <View style={styles.futureCell}>
              <CountryFlag
                countryCode={winnerCode && winnerCode !== 'xx' ? winnerCode : ''}
                kitFallback={winnerKitFallback}
                size="compact"
                accessibilityLabel={resolved.winnerCaption ?? 'Winner not picked'}
              />
            </View>
            <View style={styles.futureCell}>
              <View style={styles.leaderboardKitOuter}>
                <SoccerKit
                  size={30}
                  primary={resolved.scorerKitMeta?.kitColor ?? '#6B7280'}
                  secondary={resolved.scorerKitMeta?.trimColor ?? '#9CA3AF'}
                  number={resolved.scorerKitMeta?.number}
                />
              </View>
            </View>
          </>
        )}
      </View>
      <Text style={styles.pointsText}>{entry.points} pts</Text>
      <Text
        style={
          entry.trend === 'up'
            ? styles.trendUp
            : entry.trend === 'down'
              ? styles.trendDown
              : styles.trendSame
        }
      >
        {TREND_ICONS[entry.trend]}
      </Text>
    </View>
  );
};
